/**
 * Unified Create Collection Modal
 *
 * Shows name input in header, with Documents/Sources tabs.
 * Both tabs accumulate state independently. On save, orchestrates
 * the relevant API calls based on which tabs have content.
 */

import { Button } from '@heidi/design-system/button';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import {
  SegmentedControl,
  SegmentedControlContent,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import { TypographyCaption } from '@heidi/design-system/typography';
import type { TranslationKey } from '@heidi/runtime/i18n';
import { useTranslations } from '@heidi/runtime/i18n';
import { FileText, Globe, Loader2, X } from 'lucide-react';
import * as motion from 'motion/react-m';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { CollectionKind } from '../../../contracts';
import { libraryArtefactsMessages } from '../libraryArtefacts.messages';
import { CollectionCapDialog } from './CollectionCapDialog';
import { DocumentsStepContent } from './DocumentsStepContent';
import { InlineEditableName } from './InlineEditableName';
import { TeamToggleContent } from './TeamCollectionSwitch';
import { WebSourcesStepContent } from './WebSourcesStep';
import {
  useCreateCollectionWizard,
  type WizardTeamContext,
} from './hooks/useCreateCollectionWizard';

interface UnifiedCreateCollectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The team-scoping facts, resolved at the mount from the app's
   * `useTeamEvidenceEnforcement()`. See {@link WizardTeamContext}.
   */
  teamContext: WizardTeamContext;
  defaultTeam?: boolean;
  /** Work-output folder vs Evidence source collection. Default `input`. */
  kind?: CollectionKind;
  /** Called after a collection is successfully created.
   *
   * `domainCount` is the count of what was actually persisted (domains
   * for web, documents for docs, or the sum for unified collections).
   * `savedDomains` is the web-source slice (empty for document-only
   * collections); banner callers diff it against the original
   * recommendation. */
  onCollectionCreated?: (collection: {
    id: string;
    type: 'documents' | 'web';
    domainCount: number;
    savedDomains: string[];
  }) => void;
}

export const UnifiedCreateCollectionModal = ({
  open,
  onOpenChange,
  defaultTeam = false,
  kind = 'input',
  onCollectionCreated,
  teamContext,
}: UnifiedCreateCollectionModalProps) => {
  const { t } = useTranslations();
  const [activeTab, setActiveTab] = useState<'documents' | 'sources'>('sources');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const isArtefactFolder = kind === 'artifacts';

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const wizard = useCreateCollectionWizard({
    initialStep: 'documents',
    teamContext,
    defaultTeam,
    kind,
    onCollectionCreated,
    onClose: handleClose,
  });

  useEffect(() => {
    if (open) {
      // Re-seed on open, not just close: the modal stays mounted while the
      // user flips the library scope filter, so `defaultTeam` can change
      // between opens and the mount-time state would otherwise be stale.
      wizard.reset();
      requestAnimationFrame(() => nameInputRef.current?.focus());
    } else {
      wizard.reset();
      setActiveTab('sources');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hasDocuments =
    wizard.state.stagedFiles.length > 0 || wizard.state.selectedExistingDocs.length > 0;
  const hasSources = wizard.state.addedDomains.length > 0;
  const hasContent = hasDocuments || hasSources;

  const handleSave = async () => {
    await wizard.saveCollection();
  };

  const docsLabel = t('library.tabs.documents' as TranslationKey);
  const sourcesLabel = t('library.tabs.web' as TranslationKey);

  return (
    <DialogDrawer open={open} onOpenChange={onOpenChange}>
      <DialogDrawerContent className="w-full max-w-xl overflow-hidden p-0" hideCloseIcon>
        <motion.div
          initial={false}
          animate={{ height: isArtefactFolder ? 280 : 640 }}
          transition={{ type: 'tween', duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          className="flex max-h-[85vh] flex-col overflow-hidden p-6"
        >
          <Button
            size="lg"
            type="button"
            variant="ghost"
            iconOnly
            className="absolute end-4 top-4 size-8"
            onClick={handleClose}
            aria-label={t('common.close')}
          >
            <X size={16} />
          </Button>

          <div className="mb-5 flex items-center gap-2">
            <InlineEditableName
              ref={nameInputRef}
              value={wizard.state.name}
              placeholder={wizard.displayName}
              onChange={wizard.setName}
              className="flex-1"
            />
          </div>

          {isArtefactFolder ? (
            <TypographyCaption className="mb-auto text-text-secondary">
              <FormattedMessage {...libraryArtefactsMessages.createFolderHint} />
            </TypographyCaption>
          ) : (
            <SegmentedControl
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as 'documents' | 'sources')}
              className="min-h-0 flex-1"
            >
              <SegmentedControlList className="w-full">
                <SegmentedControlTrigger value="sources">
                  <Globe />
                  {sourcesLabel}
                </SegmentedControlTrigger>
                <SegmentedControlTrigger value="documents">
                  <FileText />
                  {docsLabel}
                </SegmentedControlTrigger>
              </SegmentedControlList>

              <SegmentedControlContent value="sources" className="flex min-h-0 flex-col">
                <WebSourcesStepContent
                  addedDomains={wizard.state.addedDomains}
                  onAddDomain={wizard.addDomain}
                  onAddDomains={wizard.addDomains}
                  onRemoveDomain={wizard.removeDomain}
                  maxDomains={wizard.maxDomains}
                />
              </SegmentedControlContent>

              <SegmentedControlContent
                value="documents"
                className="flex min-h-0 flex-col"
              >
                <DocumentsStepContent
                  stagedFiles={wizard.state.stagedFiles}
                  onAddFiles={wizard.addFiles}
                  onFilesRejected={wizard.handleFilesRejected}
                  onRemoveFile={wizard.removeFile}
                  selectedExistingDocs={wizard.state.selectedExistingDocs}
                  onAddExistingDoc={wizard.addExistingDocument}
                  onRemoveExistingDoc={wizard.removeExistingDocument}
                />
              </SegmentedControlContent>
            </SegmentedControl>
          )}

          <div className="pt-2">
            {wizard.error && (
              <TypographyCaption className="mb-3 text-validation-destructive">
                {wizard.error}
              </TypographyCaption>
            )}
            <div className="flex shrink-0 items-center justify-between gap-3">
              <div>
                <TeamToggleContent
                  isTeamCollection={wizard.state.isTeamCollection}
                  onTeamCollectionChange={wizard.setIsTeamCollection}
                  hasTeam={wizard.hasTeam}
                  canCreateTeamCollection={wizard.canCreateTeamCollection}
                  teamName={wizard.teamName}
                />
              </div>
              <div className="flex items-center gap-3">
                <Button type="button" variant="ghost" size="md" onClick={handleClose}>
                  {t('library.wizard.cancel' as TranslationKey)}
                </Button>
                <Button
                  type="button"
                  size="md"
                  onClick={() => void handleSave()}
                  disabled={(!isArtefactFolder && !hasContent) || wizard.isSaving}
                >
                  {wizard.isSaving && <Loader2 className="animate-spin" />}
                  {t('library.wizard.saveButton' as TranslationKey)}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </DialogDrawerContent>
      <CollectionCapDialog
        open={wizard.isCapDialogOpen}
        onOpenChange={(o) => !o && wizard.dismissCapDialog()}
        maxDomains={wizard.maxDomains}
      />
    </DialogDrawer>
  );
};
